/**
 * EmbedBlock — TipTap block node for `![[Target]]` and `![[image.png]]`.
 *
 * Renders as a block-level placeholder div with the embedded target
 * stored as a data-attribute. The actual asset resolution (blob URL for
 * images, transclusion for notes) is handled by the editor view in
 * VaultEditorPane.tsx — the extension is intentionally thin so the
 * Convex/Graph wiring stays out of the editor model.
 */

import { Node, mergeAttributes } from '@tiptap/core';

export interface EmbedAttrs {
  target: string;
  section: string | null;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    embed: {
      insertEmbed: (attrs: EmbedAttrs) => ReturnType;
    };
  }
}

export const EmbedBlock = Node.create({
  name: 'embed',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      target: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-target') ?? '',
        renderHTML: (attrs) => ({ 'data-target': attrs.target ?? '' }),
      },
      section: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-section'),
        renderHTML: (attrs) =>
          attrs.section ? { 'data-section': attrs.section } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div.vault-embed' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes({ class: 'vault-embed' }, HTMLAttributes),
      // Visible placeholder text — replaced by EmbedBlockView when the
      // editor mounts (a future ReactNodeView in C.6+; for first cut,
      // the placeholder is enough to round-trip the syntax).
      ['span', { class: 'vault-embed-label' }, `![[${HTMLAttributes['data-target'] ?? ''}]]`],
    ];
  },

  addCommands() {
    return {
      insertEmbed:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    };
  },
});

const EMBED_PATTERN = /!\[\[([^\]\n#]+?)(?:#([^\]\n]+))?\]\]/g;

export interface EmbedToken {
  kind: 'text' | 'embed';
  value: string;
  attrs?: EmbedAttrs;
}

export function tokenizeEmbeds(input: string): EmbedToken[] {
  const out: EmbedToken[] = [];
  let cursor = 0;
  EMBED_PATTERN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = EMBED_PATTERN.exec(input)) !== null) {
    if (cursor < m.index) {
      out.push({ kind: 'text', value: input.slice(cursor, m.index) });
    }
    out.push({
      kind: 'embed',
      value: m[0]!,
      attrs: { target: m[1]!.trim(), section: m[2]?.trim() ?? null },
    });
    cursor = m.index + m[0].length;
  }
  if (cursor < input.length) {
    out.push({ kind: 'text', value: input.slice(cursor) });
  }
  if (out.length === 0) out.push({ kind: 'text', value: input });
  return out;
}
